// Ingredients management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type { Ingredient } from '@/types/entities'
import { applyLanguageFallback } from '@/lib/language-fallback'
import { isAssistantLoggedIn } from '@/lib/permissions'

/**
 * Fetch all ingredients for a specific restaurant
 * Supports both admin users and assistants with appropriate permissions
 */
export function useIngredients(restaurantId: string | null) {
  return useQuery({
    queryKey: queryKeys.ingredients.list({ restaurantId }),
    staleTime: 5 * 60 * 1000, // 5 minutes - ingredients don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Ingredients don't need real-time updates
    refetchOnMount: false, // Don't refetch if data is fresh
    queryFn: async (): Promise<Ingredient[]> => {
      if (!restaurantId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // Use assistant RPC function
        const { data, error } = await supabase.rpc('assistant_get_ingredients', {
          
          p_restaurant_id: restaurantId,
        })

        if (error) {
          // If permission denied, return empty array instead of throwing
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view ingredients')
            return []
          }
          throw error
        }

        return (data || []).map((i: any) => ({
          id: i.id,
          restaurantId: i.restaurant_id,
          nameAr: i.name_ar,
          nameFr: i.name_fr,
          emoji: i.emoji || '🍽️',
          createdAt: i.created_at || '',
          updatedAt: i.updated_at || '',
        }))
      }

      // Admin user - use direct query
      const { data, error } = await supabase
        .from('ingredients')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('name_ar', { ascending: true })

      if (error) {
        throw error
      }

      return (data || []).map((i) => ({
        id: i.id,
        restaurantId: i.restaurant_id,
        nameAr: i.name_ar,
        nameFr: i.name_fr,
        emoji: i.emoji || '🍽️',
        createdAt: i.created_at || '',
        updatedAt: i.updated_at || '',
      }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch single ingredient by ID
 */
export function useIngredient(ingredientId: string) {
  return useQuery({
    queryKey: queryKeys.ingredients.detail(ingredientId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<Ingredient> => {
      const { data, error} = await supabase
        .from('ingredients')
        .select('*')
        .eq('id', ingredientId)
        .single()

      if (error) {
        throw error
      }

      return {
        id: data.id,
        restaurantId: data.restaurant_id,
        nameAr: data.name_ar,
        nameFr: data.name_fr,
        emoji: data.emoji || '🍽️',
        createdAt: data.created_at || '',
        updatedAt: data.updated_at || '',
      }
    },
    enabled: !!ingredientId,
  })
}

/**
 * Create new ingredient mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useCreateIngredient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      restaurantId: string
      nameAr: string
      nameFr: string
      emoji?: string
    }) => {
      // Apply fallback: if one language is missing, use the other
      const withFallback = applyLanguageFallback({
        nameAr: request.nameAr || null,
        nameFr: request.nameFr || null,
      })

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // RPC function added by migration 20251211000002
        const { data, error } = await (supabase.rpc as any)('assistant_create_ingredient', {
          
          p_restaurant_id: request.restaurantId,
          p_name_ar: withFallback.nameAr,
          p_name_fr: withFallback.nameFr,
          p_emoji: request.emoji || '🍽️',
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }
        return data
      }

      // Admin user - use direct query
      const { data, error } = await supabase
        .from('ingredients')
        .insert({
          restaurant_id: request.restaurantId,
          name_ar: withFallback.nameAr,
          name_fr: withFallback.nameFr,
          emoji: request.emoji || '🍽️',
        }as any)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data.id
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ingredients.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.ingredients.list({ restaurantId: variables.restaurantId }),
      })
    },
  })
}

/**
 * Update ingredient mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpdateIngredient(ingredientId: string, restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      nameAr?: string
      nameFr?: string
      emoji?: string
    }) => {
      // Apply fallback: if one language is missing, use the other
      const withFallback = applyLanguageFallback({
        nameAr: request.nameAr !== undefined ? request.nameAr || null : undefined,
        nameFr: request.nameFr !== undefined ? request.nameFr || null : undefined,
      } as any)

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // RPC function added by migration 20251211000002
        const { data, error } = await (supabase.rpc as any)('assistant_update_ingredient', {
          
          p_restaurant_id: restaurantId,
          p_ingredient_id: ingredientId,
          p_name_ar: withFallback.nameAr || null,
          p_name_fr: withFallback.nameFr || null,
          p_emoji: request.emoji !== undefined ? (request.emoji || null) : null,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }
        return data
      }

      // Admin user - use direct query
      const updateData: {
        name_ar?: string
        name_fr?: string
        emoji?: string | null
      } = {}

      if (withFallback.nameAr !== undefined) updateData.name_ar = withFallback.nameAr
      if (withFallback.nameFr !== undefined) updateData.name_fr = withFallback.nameFr
      if (request.emoji !== undefined) updateData.emoji = request.emoji || null

      const { data, error } = await supabase
        .from('ingredients')
        .update(updateData)
        .eq('id', ingredientId)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ingredients.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.ingredients.list({ restaurantId }),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.ingredients.detail(ingredientId),
      })
    },
  })
}

/**
 * Delete ingredient mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useDeleteIngredient(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ingredientId: string) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // RPC function added by migration 20251211000002
        const { error } = await (supabase.rpc as any)('assistant_delete_ingredient', {
          
          p_restaurant_id: restaurantId,
          p_ingredient_id: ingredientId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          // Check if it's a foreign key constraint error
          if (error.code === '23503') {
            throw new Error('INGREDIENT_IN_USE')
          }
          throw error
        }
        return
      }

      // Admin user - use direct query
      const { error } = await supabase
        .from('ingredients')
        .delete()
        .eq('id', ingredientId)

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.code === '23503') {
          throw new Error('INGREDIENT_IN_USE')
        }
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ingredients.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.ingredients.list({ restaurantId }),
      })
    },
  })
}
